import {
  createSignal,
  createMemo,
  Show,
  onMount,
  onCleanup,
} from "solid-js";
import { View, Text, hexColor } from "@lightningtv/solid";
import { useNavigate } from "@solidjs/router";
import gsap from "gsap";
import {
  setPlayListName,
  setPlayListPassword,
  setPlayListUrl,
  setPlayListUsername,
  setIsPlaying,
} from "../../state";
import LoadingIndicator from "./loading/loading";
import { initializeConfig } from "../config/index";
import { getDeviceIdentity } from "../utils/deviceIdentity";
import { clientBranding } from "../config/clientConfig";
import { warmAdParamCaches } from "../utils/mediaTailoreHelper";
import { createQrModules } from "../utils/qrCode";
import { submitRegistration, RegistrationRequest } from "../utils/registration";
import TvRegistrationForm from "../components/registration/TvRegistrationForm";
import CredentialsLogin from "../components/login/CredentialsLogin";
import HotelScreen from "../components/licence/HotelScreen";
import { decodeAccessToken, getPropertyType, UserData } from "../utils/accessToken";

// Notify Android to close the app entirely
const exitApp = () => {
  if (window.AndroidBridge && typeof (window.AndroidBridge as any).exitApp === "function") {
    (window.AndroidBridge as any).exitApp();
  } else {
    window.close();
  }
};

const LicenceScreen = () => {
  let mainContentRef;
  let actionButtonRef;     // "Continue" (success) / "Try Again" (error) — one shared card
  let noButtonRef;
  let yesButtonRef;
  let credentialsButtonRef: any; // "Login with username and password"

  const navigate = useNavigate();

  let autoNavTimer: any = null;

  const PLAYLIST_URL = "https://innplay-apps-backend-229197090060.europe-west1.run.app";
  // const API_BASE_URL = "https://device-api.applay.cloud";


  const API_BASE_URL = "https://innplay-apps-backend-229197090060.europe-west1.run.app";

  //const HARDCODED_DEVICE_ID = "A1:E0:A6:49:87:CA"; //evomedia test
  const HARDCODED_DEVICE_ID = "90:0E:B3:3F:90:00"; //applay test
  //const HARDCODED_DEVICE_ID = "75:2C:18:B9:45:77"; //tibo test

  const [authState, setAuthState] = createSignal("loading"); // loading, success, error
  const [deviceMac, setDeviceMac] = createSignal("");
  const [errorMessage, setErrorMessage] = createSignal("");
  const [isNavigating, setIsNavigating] = createSignal(false);
  const [showExitModal, setShowExitModal] = createSignal(false);
  const [showCredentialsLogin, setShowCredentialsLogin] = createSignal(false);
  // Set when /api/login succeeds for a hotel property (authState "hotel")
  const [hotelUserData, setHotelUserData] = createSignal<UserData | null>(null);
  const hotelHandle: { focus?: () => void } = {};

  // Unregistered devices can register with a phone (QR code) or on the TV
  // (form typed with the remote). Only for clients with a registrationUrl.
  const [registerMode, setRegisterMode] = createSignal<"phone" | "tv">("phone");
  const [registrationSent, setRegistrationSent] = createSignal(false);
  const tabRefs: Record<"phone" | "tv", any> = { phone: null, tv: null };
  const formHandle: { focusEntry?: () => void } = {};
  const [focusedTab, setFocusedTab] = createSignal<"phone" | "tv" | null>(null);
  const canRegister = () => !!clientBranding().registrationUrl;
  // "Register on TV" is hidden for now: with it off there are no tabs and
  // only the phone view (card + QR code) is shown. Set to true to bring it back.
  const ENABLE_TV_REGISTRATION = false;
  const showTabs = () => ENABLE_TV_REGISTRATION && canRegister() && authState() === "error";
  const showTvForm = () => showTabs() && registerMode() === "tv";

  // Close modal and restore focus to the correct main button
  const closeModal = () => {
    setShowExitModal(false);
    // Give Solid a tick to re-render, then restore focus
    setTimeout(() => {
      if (authState() === "hotel") {
        hotelHandle.focus?.();
      } else if (showTvForm()) {
        tabRefs.tv?.setFocus();
      } else if (authState() === "error" || authState() === "success") {
        (actionButtonRef as any)?.setFocus?.();
      }
    }, 50);
  };

  const ensureSessionId = () => {
    const existing = localStorage.getItem("session_id");
    if (existing) return existing;
    const sid = `sess_${Date.now()}_${Math.random().toString(16).slice(2)}`;
    localStorage.setItem("session_id", sid);
    return sid;
  };

  const authenticateDevice = async (deviceId: string, wifiMac: string) => {
    console.log(`${API_BASE_URL}/api/login`);
    const screenRes = window.AndroidBridge?.getScreenResolution?.() || "1920x1080";
    const deviceBrand = window.AndroidBridge?.getBrand?.() || window.AndroidBridge?.getManufacturer?.() || "Android TV";
    const firmware = window.AndroidBridge?.getOsVersion?.() || "unknown";
    const appVersion = window.AndroidBridge?.getAppVersion?.() || "1.0.0";

    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 15000);
      const response = await fetch(`${API_BASE_URL}/api/login`, {
        method: "POST",
        signal: controller.signal,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          screen_size: screenRes,
          screen_resolution: screenRes,
          os: "Android",
          device_brand: deviceBrand,
          device_id: deviceId, // Force the hardcoded device ID for testing
          widevine_device_id: 0,
          orbixplay_id: 0,
          wifi_mac_address: wifiMac,
          lan_mac_address: "",
          app_name: "Android TV Playlist",
          app_version: appVersion,
          ntype: 1,
          hdmi: true,
          firmware: firmware,
          api_version: "1.0",
          appid: 1001,
          player: "exo",
        }),
      });
      clearTimeout(timeoutId);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || "Authentication failed");
      }

      localStorage.setItem("screen_resolution", screenRes);
      localStorage.setItem("device_brand", deviceBrand);
      localStorage.setItem("os_version", firmware);
      localStorage.setItem("app_version", appVersion);
      localStorage.setItem("os_platform", "AndroidTV");
      localStorage.setItem("app_bundle", clientBranding().app_bundle);
      localStorage.setItem("app_name", clientBranding().appName);

      const body = await response.json();
      // Which fields came back (not their values — tokens stay out of the
      // log). The check above only rejects non-2xx statuses, so this shows
      // what the backend actually answers for a given device id.
      console.log(`🔐 /api/loginnnnnn → HTTP`, body);
      console.log(`🔐 /api/login → HTTP ${response.status}, fields:`, Object.keys(body || {}));
      return body;
    } catch (error) {
      console.error("Authentication error:", error);
      throw error;
    }
  };

  // Runs the device authentication flow. Pass deviceIdOverride to force
  // a specific device id (used by the "1234" + OK secret test code).
  const runAuthentication = async (deviceIdOverride?: string) => {
    try {
      const { deviceId, wifiMac } = await getDeviceIdentity();
      const effectiveDeviceId = deviceIdOverride || deviceId;
      //const effectiveDeviceId = HARDCODED_DEVICE_ID; // Force the hardcoded device ID for testing
      setDeviceMac(effectiveDeviceId);
      const authResponse = await authenticateDevice(effectiveDeviceId, wifiMac);
      completeLogin(effectiveDeviceId, authResponse);
    } catch (error: any) {
      console.error("Login failed:", error);
      setAuthState("error");
      setErrorMessage(error?.message || "Authentication failed");
      if (mainContentRef) (mainContentRef as any).alpha = 1;
    }
  };

  // A successful /api/login: store the tokens, set up the playlist for this
  // device id and go to Home. Used by the automatic device login and by
  // "Login with username and password" (where the username is the device id).
  const completeLogin = (effectiveDeviceId: string, authResponse: any) => {
    if (authResponse.access_token) localStorage.setItem("access_token", authResponse.access_token);
    if (authResponse.refresh_token) localStorage.setItem("refresh_token", authResponse.refresh_token);
    if (authResponse.user_id) localStorage.setItem("user_id", authResponse.user_id);
    if (authResponse.device_id) localStorage.setItem("device_id", authResponse.device_id);

    // Hotel devices stop on the hotel screen; its Continue button calls
    // finishLogin. Residential devices go straight to Home as before.
    const tokenPayload = decodeAccessToken(authResponse.access_token);
    if (getPropertyType(tokenPayload) === "hotel") {
      setHotelUserData(tokenPayload?.user_data || {});
      setDeviceMac(effectiveDeviceId);
      setAuthState("hotel");
      if (mainContentRef) (mainContentRef as any).alpha = 1;
      return;
    }

    finishLogin(effectiveDeviceId);
  };

  const finishLogin = (effectiveDeviceId: string) => {
    // Device is already registered — skip the license screen entirely
    // (no background/logo flash, no "Device Authenticated" card) and go
    // straight to Home, the same way handleEnterPlaylist would, just
    // without ever painting this screen's UI first. authState is
    // intentionally left as "loading": nothing here awaits before
    // navigate(), so there's no tick where a "success" render could
    // flash before the route actually changes.
    warmAdParamCaches();

    const cleanCredentials = effectiveDeviceId.replace(/:/g, "");
    setPlayListUrl(PLAYLIST_URL);
    setPlayListUsername(cleanCredentials);
    setPlayListPassword(cleanCredentials);
    setPlayListName("Default Playlist");

    localStorage.setItem("url", PLAYLIST_URL);
    localStorage.setItem("username", cleanCredentials);
    localStorage.setItem("password", cleanCredentials);

    initializeConfig();
    setIsPlaying(true);
    navigate("/home");
  };

  // "Login with username and password": the password must match the
  // username, which is then sent to /api/login as the device id.
  // Throws on failure so the popup can show the message.
  const handleCredentialsLogin = async (username: string, password: string) => {
    if (password !== username) throw new Error("Invalid credentials");
    const { wifiMac } = await getDeviceIdentity();
    const authResponse = await authenticateDevice(username, wifiMac);
    setShowCredentialsLogin(false);
    completeLogin(username, authResponse);
  };

  const closeCredentialsLogin = () => {
    setShowCredentialsLogin(false);
    setTimeout(() => credentialsButtonRef?.setFocus?.(), 0);
  };

  onMount(async () => {
    ensureSessionId();
    await runAuthentication();
  });

  // Secret test code: typing "1234" on the remote and then pressing OK
  // (re)authenticates using HARDCODED_DEVICE_ID instead of the real device id.
  onMount(() => {
    let codeBuffer = "";
    const onSecretCodeKey = (e: KeyboardEvent) => {
      // Digits typed into the registration or login forms aren't a code
      if (showTvForm() || showCredentialsLogin()) {
        codeBuffer = "";
        return;
      }
      if (e.key >= "0" && e.key <= "9") {
        codeBuffer = (codeBuffer + e.key).slice(-4);
        return;
      }
      if (e.key === "Enter") {
        if (codeBuffer === "1234") {
          e.stopPropagation();
          e.preventDefault();
          codeBuffer = "";
          setAuthState("loading");
          if (mainContentRef) (mainContentRef as any).alpha = 0;
          runAuthentication(HARDCODED_DEVICE_ID);
        }
        return;
      }
      codeBuffer = "";
    };
    document.addEventListener("keydown", onSecretCodeKey, true);
    onCleanup(() => document.removeEventListener("keydown", onSecretCodeKey, true));
  });

  const handleEnterPlaylist = () => {
    clearTimeout(autoNavTimer);
    if (isNavigating()) return;
    setIsNavigating(true);

    gsap.to(mainContentRef, {
      alpha: 0,
      duration: 0.25,
      ease: "power2.inOut",
      onComplete: () => {
        const credentials = deviceMac();
        const cleanCredentials = credentials.replace(/:/g, "");

        setPlayListUrl(PLAYLIST_URL);
        setPlayListUsername(cleanCredentials);
        setPlayListPassword(cleanCredentials);
        setPlayListName("Default Playlist");

        localStorage.setItem("url", PLAYLIST_URL);
        localStorage.setItem("username", cleanCredentials);
        localStorage.setItem("password", cleanCredentials);

        initializeConfig();

        setIsPlaying(true);
        navigate("/home");
        setIsNavigating(false);
      },
    });
  };

  const handleRetry = async () => {
    setAuthState("loading");
    setErrorMessage("");

    try {
      const { deviceId, wifiMac } = await getDeviceIdentity();
      setDeviceMac(deviceId);
      //setDeviceMac(HARDCODED_DEVICE_ID); // Force the hardcoded device ID for testing

      const authResponse = await authenticateDevice(deviceId, wifiMac);

      if (authResponse.access_token) localStorage.setItem("access_token", authResponse.access_token);
      if (authResponse.refresh_token) localStorage.setItem("refresh_token", authResponse.refresh_token);
      if (authResponse.user_id) localStorage.setItem("user_id", authResponse.user_id);
      if (authResponse.device_id) localStorage.setItem("device_id", authResponse.device_id);

      setAuthState("success");
      if (mainContentRef) (mainContentRef as any).alpha = 1;
    } catch (error: any) {
      setAuthState("error");
      setErrorMessage(error?.message || "Authentication failed");
      if (mainContentRef) (mainContentRef as any).alpha = 1;
      // Retried from the TV form ("Check activation"): the form was rebuilt,
      // so put focus back into it instead of the hidden "Try Again" button
      if (showTvForm()) setTimeout(() => formHandle.focusEntry?.(), 0);
    }
  };

  const handleRegistrationSubmit = async (req: RegistrationRequest) => {
    await submitRegistration(req);
    setRegistrationSent(true);
  };

  // One card for both outcomes — everything that differs between "authenticated"
  // and "not registered" lives here, so the card below is written once and
  // the state just picks a row (accent color, texts, button and what it does).
  const CARD: Record<string, { title: string; message: string; accent: string; label: string; onPress: () => void }> = {
    success: {
      title: "Device Authenticated",
      message: "Your device is authorized and ready to stream.",
      accent: "#00C46A",
      label: "Continue",
      onPress: handleEnterPlaylist,
    },
    error: {
      title: "Device Not Registered",
      message: clientBranding().registrationUrl
        ? "Scan the QR code with your phone to register this device."
        : "Your device is not authorized to access this service.",
      accent: clientBranding().primaryColor,
      label: "Try Again",
      onPress: handleRetry,
    },
  };

  // Which card to show — null while loading, so nothing renders yet.
  const cardKind = () =>
    authState() === "success" || authState() === "error" ? authState() : null;

  // Registration QR code, shown next to the "not registered" card for clients
  // that have a registrationUrl. The QR code carries the device id so the web
  // form can be filled in for the user. The printed link is the bare URL
  // (no protocol, no query string) because the user may have to type it.
  const QR_MAX_SIZE = 300;
  const registration = createMemo(() => {
    const base = clientBranding().registrationUrl;
    if (!base || authState() !== "error" || !deviceMac()) return null;
    const device = encodeURIComponent(deviceMac().replace(/:/g, ""));
    const link = `${base}${base.includes("?") ? "&" : "?"}device=${device}`;
    return {
      qr: createQrModules(link, QR_MAX_SIZE),
      displayUrl: base.replace(/^https?:\/\//, "").replace(/\/$/, ""),
    };
  });

  return (
    <View
      color={hexColor("#000000")}
      width={1920}
      height={1080}
      onEscape={() => {
        if (showCredentialsLogin()) {
          closeCredentialsLogin();
          return;
        }
        if (showExitModal()) {
          closeModal();
          return;
        }
        setShowExitModal(true);
      }}
    >
      {/* Full screen black background */}
      <View
        width={1920}
        height={1080}
        color={hexColor("#000000")}
        style={{ position: "absolute", zIndex: 0 }}
      />

      {/* Background photo + logo — always shown */}
      <View
        src={"https://i.ibb.co/kVRVhdZQ/backimg.jpg"}
        alpha={1}
        style={{
          width: 1430,
          height: 1200,
          objectFit: "cover",
          objectPosition: "center center",
        }}
        x={690}
        y={-250}
      />

      <View src={"assets/hero-gradient-mask.png"} style={{ width: 1920, height: 1080 }} />

      <View x={240} y={230} src={clientBranding().loginLogo} width={clientBranding().loginLogoWidth} height={clientBranding().loginLogoHeight} />

      {/* Main Content */}
      <View ref={mainContentRef} alpha={0}>
        <Show when={authState() === "loading"}>
          <View x={310} y={440}>
            <LoadingIndicator />
          </View>
        </Show>

        {/* Hotel property: welcome screen instead of going straight to Home */}
        <Show when={authState() === "hotel" && hotelUserData()} keyed>
          {(userData) => (
            <HotelScreen
              userData={userData}
              deviceId={deviceMac()}
              accent={clientBranding().primaryColor}
              onContinue={() => finishLogin(deviceMac())}
              screenRef={hotelHandle}
            />
          )}
        </Show>

        {/* Phone registration (status card + QR code). Hidden rather than
            unmounted while the TV form is shown: rebuilding the card would
            let "Try Again" autofocus and pull focus off the tabs. */}
        <View alpha={showTvForm() ? 0 : 1}>
          {/* keyed: a state change (e.g. error -> success after a retry) builds
            the card again, so its accent color is read fresh instead of
            depending on styles updating in place */}
          <Show when={cardKind()} keyed>
            {(kind) => {
              const c = CARD[kind as string];
              // The card's border shows the state (green/red). The Device ID box,
              // the button and the "not registered" texts use the client's brand color.
              const brand = clientBranding().primaryColor;
              return (
                <View
                  x={140}
                  y={360}
                  width={600}
                  // Taller when not registered, to fit the username login button
                  height={kind === "error" ? 650 : 560}
                  borderRadius={30}
                  style={{
                    // Soft dark panel, slightly denser toward the bottom
                    colorTop: hexColor("#000000B3"),
                    colorBottom: hexColor("#000000F2"),
                    border: { width: 2, color: hexColor(c.accent + "99") },
                  }}
                >
                  <View
                    x={50}
                    y={50}
                    style={{ display: "flex", flexDirection: "column", gap: 24, width: 500 }}
                  >
                    <Text
                      contain="width"
                      style={{ fontSize: 36, color: hexColor(kind === "error" ? brand : c.accent), fontWeight: "bold", width: 500, maxLines: 1 }}
                    >
                      {c.title}
                    </Text>

                    <Text
                      contain="width"
                      style={{ fontSize: 22, lineHeight: 30, color: hexColor("#B9B9BE"), width: 500, maxLines: 2 }}
                    >
                      {c.message}
                    </Text>

                    {/* Backend's own reason, when there is one — errorMessage was
                      being stored but never actually shown before */}
                    <Show when={kind === "error" && errorMessage()}>
                      <Text
                        contain="width"
                        style={{ fontSize: 20, color: hexColor(brand), width: 500, maxLines: 2 }}
                      >
                        {errorMessage()}
                      </Text>
                    </Show>

                    {/* Device ID — gradient from the state's accent (left) into
                      near-black (right) */}
                    <View
                      width={500}
                      height={110}
                      borderRadius={20}
                      style={{
                        colorLeft: hexColor(brand + "66"),
                        colorRight: hexColor("#1A1A1AE6"),
                        border: { width: 2, color: hexColor(brand + "80") },
                      }}
                    >
                      <Text x={28} y={20} style={{ fontSize: 18, color: hexColor("#B9B9BE"), letterSpacing: 2 }}>
                        DEVICE ID
                      </Text>
                      <Text
                        x={28}
                        y={52}
                        contain="width"
                        style={{ fontSize: 34, color: hexColor("#ffffff"), fontWeight: "bold", width: 450, maxLines: 1 }}
                      >
                        {deviceMac()}
                      </Text>
                    </View>

                    <View width={500} height={kind === "error" ? 160 : 80}>
                      <View
                        ref={(el: any) => { actionButtonRef = el; }}
                        x={125}
                        autofocus={!showTvForm()}
                        onEnter={c.onPress}
                        onUp={() => {
                          if (showTabs()) tabRefs.phone?.setFocus();
                          return true;
                        }}
                        onDown={() => {
                          credentialsButtonRef?.setFocus?.();
                          return true;
                        }}
                        style={{
                          width: 250,
                          height: 80,
                          borderRadius: 40,
                          scale: 1,
                          color: hexColor("#333333"),
                          border: { width: 3, color: hexColor("#555555") },
                          transition: {
                            scale: { duration: 200, easing: "ease-in-out" },
                            color: { duration: 200, easing: "ease-in-out" },
                          },
                          focus: {
                            scale: 1.05,
                            color: hexColor(brand),
                            border: { width: 3, color: hexColor(brand) },
                          },
                        }}
                      >
                        <Text
                          y={26}
                          contain="width"
                          textAlign="center"
                          style={{ fontSize: 26, color: hexColor("#ffffff"), fontWeight: "bold", width: 250 }}
                        >
                          {c.label}
                        </Text>
                      </View>

                      {/* Not registered: log in with a username instead of this device's id */}
                      <Show when={kind === "error"}>
                        <View
                          ref={(el: any) => { credentialsButtonRef = el; }}
                          x={30}
                          y={96}
                          onEnter={() => { setShowCredentialsLogin(true); return true; }}
                          onUp={() => { (actionButtonRef as any)?.setFocus?.(); return true; }}
                          style={{
                            width: 440,
                            height: 64,
                            borderRadius: 32,
                            scale: 1,
                            color: hexColor("#00000000"),
                            border: { width: 2, color: hexColor("#555555") },
                            transition: {
                              scale: { duration: 200, easing: "ease-in-out" },
                              color: { duration: 200, easing: "ease-in-out" },
                            },
                            focus: {
                              scale: 1.05,
                              color: hexColor(brand),
                              border: { width: 2, color: hexColor(brand) },
                            },
                          }}
                        >
                          <Text
                            y={20}
                            contain="width"
                            textAlign="center"
                            style={{ fontSize: 22, color: hexColor("#ffffff"), fontWeight: "bold", width: 440 }}
                          >
                            Login with username and password
                          </Text>
                        </View>
                      </Show>
                    </View>
                  </View>
                </View>
              );
            }}
          </Show>

          {/* Registration QR panel — right of the card, same height. The QR
            code sits on a white tile: dark-on-white with a margin around it
            is what phone cameras scan reliably from across a room. */}
          <Show when={registration()} keyed>
            {(reg) => (
              <View
                x={800}
                y={360}
                width={500}
                height={560}
                borderRadius={30}
                style={{
                  colorTop: hexColor("#000000B3"),
                  colorBottom: hexColor("#000000F2"),
                  border: { width: 2, color: hexColor("#FFFFFF33") },
                }}
              >
                <Text
                  y={44}
                  contain="width"
                  textAlign="center"
                  style={{ fontSize: 32, color: hexColor("#ffffff"), fontWeight: "bold", width: 500 }}
                >
                  Scan to register
                </Text>

                <View
                  x={(500 - (reg.qr.size + 40)) / 2}
                  y={104}
                  width={reg.qr.size + 40}
                  height={reg.qr.size + 40}
                  borderRadius={16}
                  color={hexColor("#ffffff")}
                >
                  {/* Drawn from plain rectangles, not an image: see createQrModules */}
                  <View x={20} y={20} width={reg.qr.size} height={reg.qr.size}>
                    {reg.qr.runs.map((run) => (
                      <View x={run.x} y={run.y} width={run.w} height={reg.qr.cell} color={hexColor("#000000")} />
                    ))}
                  </View>
                </View>

                <Text
                  y={470}
                  contain="width"
                  textAlign="center"
                  style={{ fontSize: 20, color: hexColor("#B9B9BE"), width: 500 }}
                >
                  or visit this link and enter your Device ID
                </Text>
                <Text
                  y={502}
                  contain="width"
                  textAlign="center"
                  style={{ fontSize: 28, color: hexColor("#ffffff"), fontWeight: "bold", width: 500, maxLines: 1 }}
                >
                  {reg.displayUrl}
                </Text>
              </View>
            )}
          </Show>
        </View>

        {/* Register on the TV: plan, fields and on-screen keyboard. Hidden
            rather than unmounted on the phone tab, so switching tabs doesn't
            throw away what was typed. */}
        <Show when={showTabs()}>
          <View alpha={showTvForm() ? 1 : 0}>
            <TvRegistrationForm
              deviceId={deviceMac()}
              accent={clientBranding().primaryColor}
              sent={registrationSent()}
              onSubmit={handleRegistrationSubmit}
              onCheckActivation={handleRetry}
              onUp={() => tabRefs.tv?.setFocus()}
              formRef={formHandle}
            />
            <Text x={1180} y={262} contain="width" textAlign="right"
              style={{ width: 600, fontSize: 22, color: hexColor("#B9B9BE") }}>
              {`DEVICE ID  ${deviceMac()}`}
            </Text>
          </View>
        </Show>

        {/* "Use phone" / "Use TV" tabs. A tab is selected as soon as it gets
            focus, so Left/Right switches between the two views. */}
        <Show when={showTabs()}>
          <View x={(1920 - (2 * 300 + 30)) / 2} y={110}>
            {(["phone", "tv"] as const).map((mode, i) => {
              const selected = () => registerMode() === mode;
              const isFocused = () => focusedTab() === mode;
              return (
                <View
                  ref={(el: any) => { tabRefs[mode] = el; }}
                  x={i * 330}
                  width={300}
                  height={70}
                  borderRadius={35}
                  color={hexColor(isFocused() ? "#FFFFFF" : selected() ? "#FFFFFF33" : "#00000000")}
                  onFocus={() => { setFocusedTab(mode); setRegisterMode(mode); }}
                  onBlur={() => setFocusedTab((t) => (t === mode ? null : t))}
                  onLeft={() => { tabRefs.phone?.setFocus(); return true; }}
                  onRight={() => { tabRefs.tv?.setFocus(); return true; }}
                  onDown={() => {
                    if (mode === "tv") formHandle.focusEntry?.();
                    else (actionButtonRef as any)?.setFocus?.();
                    return true;
                  }}
                  onEnter={() => {
                    if (mode === "tv") formHandle.focusEntry?.();
                    else (actionButtonRef as any)?.setFocus?.();
                    return true;
                  }}
                >
                  <Text y={22} contain="width" textAlign="center"
                    style={{ width: 300, fontSize: 26, fontWeight: "bold" }} color={hexColor(isFocused() ? "#000000" : "#FFFFFF")}>
                    {mode === "phone" ? "Register with phone" : "Register on TV"}
                  </Text>
                </View>
              );
            })}
          </View>
        </Show>
      </View>

      {/* ── Username / password login popup ── */}
      <Show when={showCredentialsLogin()}>
        <CredentialsLogin
          accent={clientBranding().primaryColor}
          onLogin={handleCredentialsLogin}
          onClose={closeCredentialsLogin}
        />
      </Show>

      {/* ── Exit Confirmation Modal ── */}
      <Show when={showExitModal()}>
        {/* Dimmed overlay */}
        <View
          width={1920}
          height={1080}
          color={hexColor("#000000")}
          alpha={0.75}
        />

        {/* Modal card */}
        <View
          x={660}
          y={390}
          width={600}
          height={300}
          color={hexColor("#1a1a2e")}
          borderRadius={24}
          style={{
            border: { width: 2, color: hexColor("#444466") },
          }}
        >
          {/* Title */}
          <Text
            x={30}
            y={52}
            width={600}
            style={{
              fontSize: 34,
              color: hexColor("#ffffff"),
              fontWeight: "bold",
              textAlign: "center",
            }}
          >
            Exit App?
          </Text>

          {/* Subtitle */}
          <Text
            x={30}
            y={106}
            width={600}
            style={{
              fontSize: 22,
              color: hexColor("#aaaaaa"),
              textAlign: "center",
            }}
          >
            Do you want to exit {clientBranding().appName}?
          </Text>

          {/* NO button — autofocused when modal opens */}
          <View
            ref={noButtonRef}
            x={80}
            y={190}
            width={190}
            height={68}
            borderRadius={34}
            autofocus={true}
            style={{
              color: hexColor("#2a2a3e"),
              border: { width: 3, color: hexColor("#555577") },
              focus: {
                color: hexColor("#ffffff"),
                border: { width: 3, color: hexColor("#ffffff") },
                // scale: 1.06,
              },
              transition: {
                scale: { duration: 150, easing: "ease-in-out" },
                color: { duration: 150, easing: "ease-in-out" },
              },
            }}
            onEnter={() => closeModal()}
            onRight={() => (yesButtonRef as any)?.setFocus?.()}
          >
            <Text
              x={70}
              y={22}
              width={190}
              style={{
                fontSize: 26,
                fontWeight: "bold",
                color: hexColor("#000000"),
                textAlign: "center",
              }}
            >
              No
            </Text>
          </View>

          {/* YES button */}
          <View
            ref={yesButtonRef}
            x={330}
            y={190}
            width={190}
            height={68}
            borderRadius={34}
            style={{
              color: hexColor("#2a2a3e"),
              border: { width: 3, color: hexColor("#555577") },
              focus: {
                color: hexColor("#e74c3c"),
                border: { width: 3, color: hexColor("#e74c3c") },
                // scale: 1.06,
              },
              transition: {
                scale: { duration: 150, easing: "ease-in-out" },
                color: { duration: 150, easing: "ease-in-out" },
              },
            }}
            onEnter={() => exitApp()}
            onLeft={() => (noButtonRef as any)?.setFocus?.()}
          >
            <Text
              x={70}
              y={22}
              width={190}
              style={{
                fontSize: 26,
                fontWeight: "bold",
                color: hexColor("#ffffff"),
                textAlign: "center",
              }}
            >
              Yes
            </Text>
          </View>
        </View>
      </Show>
    </View>
  );
};

export default LicenceScreen;